import { termsFor } from './terms'
import { hiddenWallets } from './wallets'

let stripes = {}
let stripeElements = {}
// Client secrets whose Element was built in deferred mode — see getStripeElements.
const deferredSetupSecrets = new Set()
const paymentMethodAvailabilityListeners = new WeakMap()

// Returns a Stripe object for the passed publishable key.
export async function getStripe(publishableKey) {
  if (stripes[publishableKey]) {
    return stripes[publishableKey]
  }

  let Stripe = await getStripeClass()

  stripes[publishableKey] = Stripe(publishableKey)
  return stripes[publishableKey]
}

// setupCurrency is only set for a SetupIntent, and only when we know what the later charge will be in. A SetupIntent
// has no currency of its own — Stripe has no API field for one — so an Element built from its client secret is offered
// every method the account has, including ones that cannot take that currency: a EUR trial was shown Klarna and then
// refused at confirm. Naming the currency to Elements is the only way to tell Stripe, and it filters the offer itself.
// Without a currency (a card update, say) we build from the client secret exactly as before.
async function getStripeElements(
  publishableKey,
  clientSecret,
  setupCurrency,
  excludedTypes = []
) {
  const elementsCacheKey = publishableKey + clientSecret
  if (stripeElements[elementsCacheKey]) {
    return stripeElements[elementsCacheKey]
  }
  let stripe = await getStripe(publishableKey)

  const initialTheme = document.documentElement.classList.contains('dark')
    ? 'night'
    : 'stripe'

  const elementsOptions = setupCurrency
    ? {
        mode: 'setup',
        currency: setupCurrency,
        setupFutureUsage: 'off_session',
        ...(excludedTypes.length
          ? { excludedPaymentMethodTypes: excludedTypes }
          : {}),
      }
    : { clientSecret: clientSecret }

  // A deferred Element holds no client secret, so confirming one has to be told which intent it belongs to, and has to
  // validate through elements.submit() first. Remembered here rather than threaded through every caller's signature.
  if (setupCurrency) {
    deferredSetupSecrets.add(clientSecret)
  }

  stripeElements[elementsCacheKey] = stripe.elements({
    ...elementsOptions,
    appearance: {
      theme: initialTheme,
    },
  })

  changeThemeListener(stripeElements[elementsCacheKey])

  return stripeElements[elementsCacheKey]
}

function changeThemeListener(elements) {
  const observeHtmlClassChanges = () => {
    const htmlElement = document.documentElement

    const observer = new MutationObserver((mutations) => {
      mutations.forEach((mutation) => {
        if (
          mutation.type === 'attributes' &&
          mutation.attributeName === 'class'
        ) {
          const theme = htmlElement.classList.contains('dark')
            ? 'night'
            : 'stripe'

          elements.update({ appearance: { theme } })
        }
      })
    })

    observer.observe(htmlElement, {
      attributes: true,
      attributeFilter: ['class'],
    })
  }

  observeHtmlClassChanges()
}

// Long enough for a slow phone connection, short enough that the caller can say something instead
// of leaving the buyer on a spinner.
const STRIPE_JS_LOAD_TIMEOUT_MS = 15000

// One shared load attempt. The previous version appended a fresh <script> on every call and settled
// only on `onload`, so a js.stripe.com that never loads — ad blocker, privacy extension, CSP,
// offline — left the promise pending forever. initializeStripeElement then never returned and the
// checkout stayed dimmed with no error. Reject instead, and let the caller paint something.
let stripeClassPromise = null

function getStripeClass() {
  if (typeof Stripe !== 'undefined') {
    return Promise.resolve(window.Stripe)
  }
  if (stripeClassPromise) {
    return stripeClassPromise
  }

  stripeClassPromise = new Promise((resolve, reject) => {
    let script = document.createElement('script')

    // Cleared so a retry (or another payment processor) gets a fresh attempt rather than the
    // rejected promise forever.
    function fail(message) {
      clearTimeout(timeout)
      stripeClassPromise = null
      reject(new Error(message))
    }

    let timeout = setTimeout(
      () => fail('Stripe.js did not load in time'),
      STRIPE_JS_LOAD_TIMEOUT_MS
    )

    script.onload = () => {
      clearTimeout(timeout)
      if (typeof window.Stripe === 'undefined') {
        fail('Stripe.js loaded without defining Stripe')
        return
      }
      resolve(window.Stripe)
    }
    script.onerror = () => fail('Stripe.js failed to load')
    script.src = 'https://js.stripe.com/v3/'
    document.head.appendChild(script)
  })

  return stripeClassPromise
}

export async function initializeStripeElement(
  publishableKey,
  clientSecret,
  targetSelector,
  {
    automaticPaymentMethods = false,
    setupCurrency = null,
    futureCharge = false,
    excludedTypes = [],
    excludedWallets = [],
  } = {}
) {
  const target = document.querySelector(targetSelector)
  const defaultValues = getDefaultValues(target)
  let elements = await getStripeElements(
    publishableKey,
    clientSecret,
    setupCurrency,
    excludedTypes
  )

  const fieldsBillingDetailsConfig = {
    name: 'never',
    email: 'never',
  }

  // Check the field with the country code selector is actually present in the form
  let countryCodeField
  if (target.dataset.countryCodeSelector) {
    countryCodeField = document.querySelector(
      target.dataset.countryCodeSelector
    )
  }
  if (countryCodeField) {
    fieldsBillingDetailsConfig.address = {
      country: 'never',
    }
  }

  if (target.dataset.postalCodeSelector) {
    fieldsBillingDetailsConfig.address =
      fieldsBillingDetailsConfig.address || {}
    fieldsBillingDetailsConfig.address.postalCode = 'never'
  }

  const options = {
    defaultValues: defaultValues,
    fields: {
      billingDetails: fieldsBillingDetailsConfig,
    },
  }

  // When Stripe picks the methods we can't know which mandates the buyer has to agree to, and several of them
  // (SEPA Direct Debit, BECS, …) are only legal to charge once that text has been shown. So only silence the
  // methods our own terms box already covers and let Stripe render whatever the rest require.
  options.terms = termsFor(automaticPaymentMethods, futureCharge)

  const wallets = hiddenWallets(excludedWallets)
  if (Object.keys(wallets).length) options.wallets = wallets

  let paymentElement =
    elements.getElement('payment') || elements.create('payment', options)

  watchBillingDetails(target, paymentElement)

  const previousAvailabilityListener =
    paymentMethodAvailabilityListeners.get(paymentElement)
  if (previousAvailabilityListener) {
    paymentElement.off(
      'availablepaymentmethodschange',
      previousAvailabilityListener
    )
    paymentMethodAvailabilityListeners.delete(paymentElement)
  }
  if (automaticPaymentMethods) {
    const listener = ({ paymentMethods }) => {
      target.dispatchEvent(
        new CustomEvent('stripe:availablepaymentmethodschange', {
          detail: { clientSecret, paymentMethods },
        })
      )
    }
    paymentMethodAvailabilityListeners.set(paymentElement, listener)
    paymentElement.on('availablepaymentmethodschange', listener)
  }

  // Registered before mount so the event cannot be missed. Stripe fires this when it cannot load
  // the payment form — most importantly when the publishable key cannot read this client_secret,
  // which is what a key pair from two different Stripe accounts produces. `ready` never fires in
  // that case, so this is the only signal the caller gets that the form is never coming.
  //
  // clientSecret rides along on both events because every caller mounts into the same shared
  // #stripe_card_element: it is what lets a listener tell this mount's event from one arriving late
  // from a mount the buyer has already moved on from.
  paymentElement.on('loaderror', (event) => {
    target.dispatchEvent(
      new CustomEvent('stripe:loaderror', {
        detail: { clientSecret, error: event && event.error },
      })
    )
  })

  paymentElement.mount(targetSelector)

  paymentElement.on('ready', () => {
    // If we are setting a new card element for the user to enter a new card,
    // we want to add a class that marks it as not completed, to prevent the form from being submitted,
    // lowering the amount of abandoned purchases.
    // This class will be removed once the user fills in all the required card details.
    // Also, sometimes this 'ready' event is fired AFTER the below 'change' event for some reason.
    // So if in the 'change' event we determine that the card should be marked as completed
    // (i.e. iDeal was selected, which doesn't have input fields), do not mark it as NOT completed here.
    if (
      targetSelector == '#stripe_card_element' &&
      !target.classList.contains('js-stripe-is-completed')
    ) {
      target.classList.add('js-stripe-is-not-completed')
    }

    target.dispatchEvent(
      new CustomEvent('stripe:ready', { detail: { clientSecret } })
    )
  })

  paymentElement.on('change', (event) => {
    const stripeCardElement = document.querySelector('#stripe_card_element')
    if (stripeCardElement !== target) return

    if (event.complete) {
      stripeCardElement.classList.remove('js-stripe-is-not-completed')
      stripeCardElement.classList.add('js-stripe-is-completed')
    } else {
      stripeCardElement.classList.add('js-stripe-is-not-completed')
      stripeCardElement.classList.remove('js-stripe-is-completed')
    }
  })
}

// Name and email are here because the Element is told we supply them (fields.billingDetails above),
// and us_bank_account needs both while it collects the account — its Financial Connections flow
// never reports the Element complete without them. sepa_debit needs them only at confirm, which is
// why ACH was the one method that could never be paid with.
function getDefaultValues(target) {
  const fields = document.querySelector('#stripe_fields')?.dataset || {}

  return {
    billingDetails: {
      name: fieldValues(target.dataset.nameSelector) || fields.customerName,
      email: fieldValues(target.dataset.emailSelector) || fields.customerEmail,
      address: {
        country: fields.addressCountry,
        postal_code: fields.addressPostalCode,
      },
    },
  }
}

// Stripe is told we supply name and email (fields.billingDetails above), so whatever reaches confirm is all it gets —
// and a blank one is what refuses a SEPA debit there. The values the submit button was rendered with can predate the
// buyer typing them: on the signup flows the contact fields sit in the same form as the Payment Element, and the page
// is rendered before there is an owner to name. So fall back to what those fields hold now.
function billingDetailsFor(selector, name, email) {
  const target = document.querySelector(selector)
  const live = target ? getDefaultValues(target).billingDetails : {}

  return { name: name || live.name, email: email || live.email }
}

// Joined rather than read from one field, because a checkout configured for separate first and last
// name fields has two inputs behind Stripe's single name. Empty on the steps that render no contact
// fields at all, and then the server-rendered value on #stripe_fields is used instead.
function fieldValues(selector) {
  if (!selector) return ''

  return Array.from(document.querySelectorAll(selector))
    .map((field) => field.value.trim())
    .filter(Boolean)
    .join(' ')
}

// The buyer can keep editing name and email after the Element has mounted — on one-page checkout
// they sit right above it — and only an email edit re-renders the payment form, so a name edit would
// otherwise never reach Stripe. One delegated listener aimed at whichever Element is mounted now:
// the contact fields outlive the payment form's re-renders, so per-mount listeners would pile up on
// them and keep writing into Elements the buyer has already moved on from.
let billingDetailsWatch = null

function watchBillingDetails(target, paymentElement) {
  const selector = [target.dataset.nameSelector, target.dataset.emailSelector]
    .filter(Boolean)
    .join(', ')
  if (!selector) return

  const alreadyListening = billingDetailsWatch !== null

  // name and email start unknown rather than at what this mount was given: `elements.create` is
  // skipped when an Element already exists for this intent, and that one kept the values it was
  // created with. Unknown means the first edit always pushes, which resyncs either way.
  billingDetailsWatch = {
    target,
    paymentElement,
    selector,
    name: null,
    email: null,
  }

  if (!alreadyListening) document.addEventListener('change', pushBillingDetails)
}

function pushBillingDetails(event) {
  const watch = billingDetailsWatch
  if (!event.target.matches?.(watch.selector)) return

  const defaultValues = getDefaultValues(watch.target)
  const { name, email } = defaultValues.billingDetails
  if (name === watch.name && email === watch.email) return

  watch.name = name
  watch.email = email
  watch.paymentElement.update({ defaultValues })
}

/* Handles a client-side submit for a new card. Note this requires Stripe Elements to be mounted if using a payment
   method that requires it. */
export async function submitToStripe(
  publishableKey,
  clientSecret,
  successUrl,
  setupIntent,
  subtype,
  name,
  email,
  selector
) {
  let stripe = await getStripe(publishableKey)

  if (subtype == 'bancontact') {
    /* No Stripe elements for Bancontact, we get right to calling Stripe, which will either return an error or redirect
       away. */
    const { error } = await stripe.confirmBancontactPayment(clientSecret, {
      payment_method: {
        billing_details: {
          name: name,
          email: email,
        },
      },
      return_url: successUrl,
    })

    return error
  }

  let elements = await getStripeElements(publishableKey, clientSecret)

  const billing_details = billingDetailsFor(selector, name, email)

  const countryCodeSelector =
    document.querySelector(selector).dataset.countryCodeSelector
  const countryField = document.querySelector(countryCodeSelector)
  if (countryField) {
    billing_details.address = {
      country: document.querySelector(countryCodeSelector).value,
    }
  }

  const postalCodeSelector =
    document.querySelector(selector).dataset.postalCodeSelector
  if (postalCodeSelector) {
    billing_details.address = billing_details.address || {}
    billing_details.address.postal_code =
      document.querySelector(postalCodeSelector).value
  }

  let confirmParams = {
    return_url: successUrl,
    payment_method_data: {
      billing_details,
    },
  }

  if (setupIntent) {
    // A deferred Element (one told the currency, see getStripeElements) carries no client secret and has not been
    // validated yet, so it needs both: submit() first, then the intent named explicitly. An Element built the old way
    // already knows its intent, and calling submit() on one is an error — hence the split.
    if (deferredSetupSecrets.has(clientSecret)) {
      const { error: submitError } = await elements.submit()
      if (submitError) return submitError

      const { error } = await stripe.confirmSetup({
        elements,
        clientSecret,
        confirmParams: confirmParams,
      })

      return error
    }

    const { error } = await stripe.confirmSetup({
      elements,
      confirmParams: confirmParams,
    })

    return error
  }

  const { error } = await stripe.confirmPayment({
    elements,
    confirmParams: confirmParams,
  })

  return error
}
window.submitToStripe = submitToStripe

/* Handles a client-side submit for a saved card. Note this does not require Stripe Elements to be mounted. */
async function confirmExisting3DS(
  publishableKey,
  clientSecret,
  paymentMethodId,
  successUrl
) {
  let stripe = await getStripe(publishableKey)

  let confirmParams = {
    payment_method: paymentMethodId,
    return_url: successUrl,
  }

  return stripe.confirmCardPayment(clientSecret, confirmParams)
}

/* Handles Stripe client-side submit. Either navigates to the success URL and returns null or returns a String error. */
export async function handleClientSideSubmitResult(
  json,
  publishableKey,
  subtype,
  customerName,
  customerEmail
) {
  if (json.success) {
    // It worked, no client-side stuff required.
    location.href = json.callback_url
    return
  }

  if (json.redirect_url) {
    // The stored method needs the buyer to approve with their provider; Stripe brings them back
    // through the transaction's return_url.
    location.href = json.redirect_url
    return
  }

  if (!json.client_secret) {
    return 'Unknown error'
  }

  if (json.payment_method_id) {
    // A previously existing payment method that needs 3DS reconfirmation. This method does not require
    // Stripe Elements to be set up.
    return await confirmExisting3DS(
      publishableKey,
      json.client_secret,
      json.payment_method_id,
      json.callback_url
    ).then((result) => {
      if (result.paymentIntent) {
        location.href = json.callback_url
        return
      }
      console.log(result)
      return result.error.message
    })
  }

  // A new payment method that needs 3DS confirmation.
  return submitToStripe(
    publishableKey,
    json.client_secret,
    json.callback_url,
    json.intent_type,
    subtype,
    customerName,
    customerEmail,
    '#stripe_card_element'
  )
}
